import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../../lib/api";
import {
  ShieldCheck,
  Warning,
  Lock,
  EnvelopeSimple,
  ArrowsClockwise,
  ShieldWarning,
  Check,
  ArrowCounterClockwise,
  Bell,
} from "@phosphor-icons/react";

/**
 * LoginDigestCard — admin Overview tile.
 *
 * Pulls /api/admin/security/login-digest and surfaces auth-related
 * activity from the last 24h at a glance:
 *  - Sign-ins, failed-credential attempts, account lockouts
 *  - Password reset requests / completions / email failures
 *  - HIBP-rejected new passwords
 *  - Hourly attempt sparkline
 *
 * Defaults to a 24h window. Click the chip group to switch to 7d.
 */

const TONE_CLASSES = {
  ok:      "bg-emerald-50 border-emerald-200 text-emerald-800",
  warn:    "bg-amber-50  border-amber-200  text-amber-800",
  bad:     "bg-rose-50   border-rose-200   text-rose-800",
  neutral: "bg-stone-50  border-stone-200  text-[#4A5C50]",
};

const TONE_DOT = {
  ok: "bg-emerald-500",
  warn: "bg-amber-500",
  bad: "bg-rose-500",
  neutral: "bg-stone-400",
};

function Sparkline({ data, height = 40 }) {
  if (!data || data.length < 2) {
    return (
      <div className="text-[10px] text-[#4A5C50] italic">No activity in this window</div>
    );
  }
  const max = Math.max(1, ...data.map((d) => d.count));
  const width = Math.max(120, data.length * 8);
  const step = width / Math.max(1, data.length - 1);
  const points = data.map((d, i) => {
    const x = i * step;
    const y = height - (d.count / max) * (height - 4) - 2;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  return (
    <svg width={width} height={height} className="block" data-testid="spark-svg" aria-hidden>
      <polyline
        fill="none"
        stroke="#184A30"
        strokeWidth="1.5"
        strokeLinejoin="round"
        strokeLinecap="round"
        points={points.join(" ")}
      />
      {data.map((d, i) => {
        const x = i * step;
        const y = height - (d.count / max) * (height - 4) - 2;
        return (
          <circle
            key={i}
            cx={x}
            cy={y}
            r={d.fails > 0 ? 2.4 : 1.4}
            fill={d.fails > 0 ? "#E11D48" : "#184A30"}
          />
        );
      })}
    </svg>
  );
}

function MetricRow({ icon: Icon, label, count, tone, testid }) {
  return (
    <div
      className={`flex items-center justify-between gap-3 rounded-xl border px-3 py-2.5 ${TONE_CLASSES[tone]}`}
      data-testid={testid}
    >
      <div className="flex items-center gap-2 min-w-0">
        <Icon size={16} weight="duotone" className="shrink-0" />
        <span className="text-xs truncate">{label}</span>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <span className={`inline-block w-1.5 h-1.5 rounded-full ${TONE_DOT[tone]}`} />
        <span className="serif text-xl text-[#112318] tabular-nums">{count}</span>
      </div>
    </div>
  );
}

export default function LoginDigestCard() {
  const [hours, setHours] = useState(24);
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [acking, setAcking] = useState(null); // anomaly key currently being mutated

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/security/login-digest?hours=${hours}`);
      setD(r.data);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [hours]);

  const acknowledge = async (key) => {
    setAcking(key);
    // Optimistic — remove from anomalies, push to acknowledged_anomalies
    setD((prev) => {
      if (!prev) return prev;
      const moving = (prev.anomalies || []).find((a) => a.key === key);
      return {
        ...prev,
        anomalies: (prev.anomalies || []).filter((a) => a.key !== key),
        acknowledged_anomalies: [
          ...(prev.acknowledged_anomalies || []),
          ...(moving
            ? [{ ...moving, acknowledged_at: new Date().toISOString(), acknowledged_by: "you" }]
            : []),
        ],
      };
    });
    try {
      await api.post(`/admin/security/anomalies/${key}/acknowledge`);
      // Re-fetch to reconcile with server truth (acknowledged_by email, etc.)
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
      await load();
    } finally {
      setAcking(null);
    }
  };

  const clearAck = async (key) => {
    setAcking(key);
    try {
      await api.delete(`/admin/security/anomalies/${key}/acknowledge`);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setAcking(null);
    }
  };

  return (
    <div
      className="bg-white border border-[#E5E0D8] rounded-2xl p-6"
      data-testid="login-digest-card"
    >
      <div className="flex items-baseline justify-between gap-3 flex-wrap mb-4">
        <div>
          <h3 className="serif text-2xl text-[#112318] flex items-center gap-2">
            <ShieldCheck size={22} weight="duotone" className="text-[#184A30]" />
            Sign-in digest
          </h3>
          <p className="text-xs text-[#4A5C50] mt-1">
            Auth events in the last {hours === 24 ? "24 hours" : `${hours / 24} days`}
          </p>
        </div>
        <div className="flex items-center gap-2 text-[11px]">
          <div className="inline-flex rounded-full border border-[#E5E0D8] overflow-hidden" data-testid="digest-window-toggle">
            {[
              { v: 24, l: "24h" },
              { v: 24 * 7, l: "7d" },
              { v: 24 * 30, l: "30d" },
            ].map((b) => (
              <button
                key={b.v}
                onClick={() => setHours(b.v)}
                disabled={loading}
                className={`px-2.5 py-1 transition-colors ${
                  hours === b.v ? "bg-[#184A30] text-white" : "bg-white text-[#4A5C50] hover:bg-[#FBF9F6]"
                }`}
                data-testid={`digest-window-${b.l}`}
              >
                {b.l}
              </button>
            ))}
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="rounded-full border border-[#E5E0D8] bg-white text-[#4A5C50] px-2.5 py-1 hover:bg-[#FBF9F6] disabled:opacity-50"
            data-testid="digest-refresh"
            aria-label="Refresh"
          >
            <ArrowsClockwise size={12} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-rose-300 bg-rose-50 p-3 text-xs text-rose-800" data-testid="digest-error">
          {error}
        </div>
      )}

      {/* Anomaly alert banner — fires when failed_creds, lockouts, or HIBP
          rejections breach env-tunable thresholds. Severity-coloured by the
          worst rule currently triggered. */}
      {!error && d?.anomalies?.length > 0 && (
        <div
          className={`mb-4 rounded-xl border p-3.5 ${
            d.anomalies.some((a) => a.severity === "bad")
              ? "bg-rose-50 border-rose-300"
              : "bg-amber-50 border-amber-300"
          }`}
          data-testid="digest-anomaly-banner"
        >
          <div className="flex items-start gap-2">
            <ShieldWarning
              size={18}
              weight="fill"
              className={`shrink-0 mt-0.5 ${
                d.anomalies.some((a) => a.severity === "bad") ? "text-rose-600" : "text-amber-600"
              }`}
            />
            <div className="min-w-0 flex-1">
              <p
                className={`text-sm font-semibold ${
                  d.anomalies.some((a) => a.severity === "bad") ? "text-rose-900" : "text-amber-900"
                }`}
                data-testid="anomaly-banner-title"
              >
                {d.anomalies.length} anomaly{d.anomalies.length === 1 ? "" : " alerts"} triggered
              </p>
              <ul className="mt-2 space-y-2">
                {d.anomalies.map((a) => (
                  <li
                    key={a.key}
                    className="text-xs leading-relaxed flex items-start gap-2"
                    data-testid={`anomaly-${a.key}`}
                  >
                    <div className="min-w-0 flex-1">
                      <span
                        className={`font-semibold ${
                          a.severity === "bad" ? "text-rose-800" : "text-amber-800"
                        }`}
                      >
                        {a.title}:
                      </span>{" "}
                      <span className="text-[#4A5C50]">{a.detail}</span>
                    </div>
                    <button
                      onClick={() => acknowledge(a.key)}
                      disabled={acking === a.key}
                      className={`shrink-0 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors disabled:opacity-50 ${
                        a.severity === "bad"
                          ? "bg-white border border-rose-300 text-rose-800 hover:bg-rose-100"
                          : "bg-white border border-amber-300 text-amber-800 hover:bg-amber-100"
                      }`}
                      data-testid={`anomaly-ack-${a.key}`}
                      title={`Silence this alert for ${d.thresholds?.ack_ttl_hours || 24}h`}
                    >
                      <Check size={11} weight="bold" />
                      {acking === a.key ? "Saving…" : "Acknowledge"}
                    </button>
                  </li>
                ))}
              </ul>
              <p className="text-[10px] text-[#4A5C50] mt-2 italic">
                Acknowledging silences the alert for{" "}
                {d.thresholds?.ack_ttl_hours || 24}h (no email + no banner). Otherwise a
                deduplicated ops email is sent on the next 15-minute tick (max once per 6h per
                rule).{" "}
                <Link
                  to="/admin/notification-preferences"
                  className="not-italic font-semibold underline hover:no-underline"
                  data-testid="prefs-link"
                >
                  <Bell size={9} weight="fill" className="inline mr-0.5 -mt-0.5" />
                  Tune in Notification Preferences
                </Link>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Acknowledged anomalies — collapsed list with re-arm button */}
      {!error && d?.acknowledged_anomalies?.length > 0 && (
        <div
          className="mb-4 rounded-xl border border-[#E5E0D8] bg-[#FBF9F6] p-3"
          data-testid="acknowledged-anomalies"
        >
          <p className="text-[10px] tracking-[0.15em] uppercase text-[#4A5C50] mb-2 flex items-center gap-1.5">
            <Check size={11} weight="bold" className="text-emerald-700" />
            Acknowledged ({d.acknowledged_anomalies.length})
          </p>
          <ul className="space-y-1.5">
            {d.acknowledged_anomalies.map((a) => (
              <li
                key={a.key}
                className="flex items-start gap-2 text-[11px]"
                data-testid={`acknowledged-${a.key}`}
              >
                <div className="min-w-0 flex-1">
                  <span className="font-semibold text-[#112318]">{a.title}</span>{" "}
                  <span className="text-[#4A5C50]">
                    · silenced by {a.acknowledged_by || "admin"}
                  </span>
                </div>
                <button
                  onClick={() => clearAck(a.key)}
                  disabled={acking === a.key}
                  className="shrink-0 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold text-[#4A5C50] border border-[#E5E0D8] bg-white hover:bg-[#FBF9F6] disabled:opacity-50"
                  data-testid={`anomaly-rearm-${a.key}`}
                  title="Re-arm this alert"
                >
                  <ArrowCounterClockwise size={10} weight="bold" />
                  {acking === a.key ? "…" : "Re-arm"}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {!error && d && (
        <>
          {/* Headline strip */}
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="rounded-xl bg-[#FBF9F6] border border-[#E5E0D8] px-3 py-2.5" data-testid="digest-headline-attempts">
              <p className="text-[10px] tracking-[0.15em] uppercase text-[#4A5C50]">Attempts</p>
              <p className="serif text-2xl text-[#112318] tabular-nums">{d.totals.attempts}</p>
            </div>
            <div
              className={`rounded-xl px-3 py-2.5 border ${
                d.totals.success_rate >= 95
                  ? "bg-emerald-50 border-emerald-200"
                  : d.totals.success_rate >= 80
                  ? "bg-amber-50 border-amber-200"
                  : "bg-rose-50 border-rose-200"
              }`}
              data-testid="digest-headline-success-rate"
            >
              <p className="text-[10px] tracking-[0.15em] uppercase text-[#4A5C50]">Success</p>
              <p className="serif text-2xl text-[#112318] tabular-nums">{d.totals.success_rate}%</p>
            </div>
            <div
              className={`rounded-xl px-3 py-2.5 border ${
                d.totals.alerts > 0
                  ? "bg-rose-50 border-rose-200"
                  : "bg-emerald-50 border-emerald-200"
              }`}
              data-testid="digest-headline-alerts"
            >
              <p className="text-[10px] tracking-[0.15em] uppercase text-[#4A5C50]">Alerts</p>
              <p className="serif text-2xl text-[#112318] tabular-nums">{d.totals.alerts}</p>
            </div>
          </div>

          {/* Sparkline */}
          {d.spark && d.spark.length > 0 && (
            <div className="mb-5">
              <p className="text-[10px] tracking-[0.15em] uppercase text-[#4A5C50] mb-2">Hourly attempts</p>
              <div className="overflow-x-auto">
                <Sparkline data={d.spark} />
              </div>
            </div>
          )}

          {/* Per-bucket grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="digest-buckets">
            <MetricRow icon={ShieldCheck}      label={d.buckets.signins.label}       count={d.buckets.signins.count}       tone={d.buckets.signins.tone}       testid="bucket-signins" />
            <MetricRow icon={Warning}          label={d.buckets.failed_creds.label}  count={d.buckets.failed_creds.count}  tone={d.buckets.failed_creds.tone}  testid="bucket-failed-creds" />
            <MetricRow icon={Lock}             label={d.buckets.lockouts.label}      count={d.buckets.lockouts.count}      tone={d.buckets.lockouts.tone}      testid="bucket-lockouts" />
            <MetricRow icon={ShieldCheck}      label={d.buckets.registrations.label} count={d.buckets.registrations.count} tone={d.buckets.registrations.tone} testid="bucket-registrations" />
            <MetricRow icon={EnvelopeSimple}   label={d.buckets.pw_reset_req.label}  count={d.buckets.pw_reset_req.count}  tone={d.buckets.pw_reset_req.tone}  testid="bucket-pw-reset-req" />
            <MetricRow icon={ShieldCheck}      label={d.buckets.pw_reset_done.label} count={d.buckets.pw_reset_done.count} tone={d.buckets.pw_reset_done.tone} testid="bucket-pw-reset-done" />
            <MetricRow icon={Warning}          label={d.buckets.pw_reset_fail.label} count={d.buckets.pw_reset_fail.count} tone={d.buckets.pw_reset_fail.tone} testid="bucket-pw-reset-fail" />
            <MetricRow icon={Warning}          label={d.buckets.gate_blocks.label}   count={d.buckets.gate_blocks.count}   tone={d.buckets.gate_blocks.tone}   testid="bucket-gate-blocks" />
            <MetricRow icon={Warning}          label={d.buckets.hibp_rejected.label} count={d.buckets.hibp_rejected.count} tone={d.buckets.hibp_rejected.tone} testid="bucket-hibp" />
          </div>

          <div className="mt-4 text-right">
            <span className="text-[10px] text-[#4A5C50]" data-testid="digest-footnote">
              See the <strong>Login logs</strong> tab above for individual rows.
            </span>
          </div>
        </>
      )}
    </div>
  );
}
